/**
 * ProjectDocumentsTab - Onglet documents dans le détail projet
 * Sprint 3.3 - US-3.3.3: Documents Chantier Partagés
 *
 * Design System: Dashboard (text-gray-900, bg-white/70)
 */

import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  File,
  Download,
  Search,
  Filter,
  FolderOpen,
  HardDrive,
  Calendar,
  User,
  ChevronDown,
  X,
} from 'lucide-react';
import { useProjectDocuments, useProjectDocumentStats } from '../hooks/useClientPortal';
import type { ClientDocument, ClientDocumentType, ClientDocumentsFilter } from '../types/client-portal.types';

interface ProjectDocumentsTabProps {
  projectId: string;
}

// Clés i18n des types de documents
const DOCUMENT_TYPE_KEYS: Record<ClientDocumentType, string> = {
  CONTRACT: 'common.clientPortal.documents.types.CONTRACT',
  PLAN: 'common.clientPortal.documents.types.PLAN',
  QUOTE: 'common.clientPortal.documents.types.QUOTE',
  INVOICE: 'common.clientPortal.documents.types.INVOICE',
  PHOTO: 'common.clientPortal.documents.types.PHOTO',
  REPORT: 'common.clientPortal.documents.types.REPORT',
  PERMIT: 'common.clientPortal.documents.types.PERMIT',
  INSURANCE: 'common.clientPortal.documents.types.INSURANCE',
  OTHER: 'common.clientPortal.documents.types.OTHER',
};

// Icônes des types de documents
function getDocumentIcon(type: ClientDocumentType) {
  switch (type) {
    case 'PHOTO':
      return <FileImage className="w-5 h-5" />;
    case 'PLAN':
      return <FileSpreadsheet className="w-5 h-5" />;
    case 'CONTRACT':
    case 'QUOTE':
    case 'INVOICE':
    case 'REPORT':
      return <FileText className="w-5 h-5" />;
    default:
      return <File className="w-5 h-5" />;
  }
}

// Couleurs des badges par type
function getTypeBadgeColor(type: ClientDocumentType): string {
  switch (type) {
    case 'CONTRACT':
      return 'bg-blue-100 text-blue-700';
    case 'PLAN':
      return 'bg-purple-100 text-purple-700';
    case 'QUOTE':
      return 'bg-amber-100 text-amber-700';
    case 'INVOICE':
      return 'bg-teal-100 text-teal-700';
    case 'PHOTO':
      return 'bg-pink-100 text-pink-700';
    case 'REPORT':
      return 'bg-indigo-100 text-indigo-700';
    case 'PERMIT':
      return 'bg-green-100 text-green-700';
    case 'INSURANCE':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// Formater la taille du fichier
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Formater la date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ProjectDocumentsTab({ projectId }: ProjectDocumentsTabProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ClientDocumentsFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { data: documents, isLoading, error } = useProjectDocuments(projectId, filter);
  const { data: stats } = useProjectDocumentStats(projectId);

  // Appliquer la recherche avec debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    // Debounce de 300ms avant d'appliquer le filtre
    const timeoutId = setTimeout(() => {
      setFilter(prev => ({ ...prev, search: value || undefined }));
    }, 300);
    return () => clearTimeout(timeoutId);
  }, []);

  // Filtrer par type
  const handleTypeFilter = useCallback((type: ClientDocumentType | undefined) => {
    setFilter(prev => ({ ...prev, documentType: type }));
    setShowFilters(false);
  }, []);

  // Réinitialiser les filtres
  const clearFilters = useCallback(() => {
    setFilter({});
    setSearchValue('');
    setShowFilters(false);
  }, []);

  // Grouper les documents par type pour l'affichage
  const groupedDocuments = useMemo(() => {
    if (!documents) return new Map<ClientDocumentType, ClientDocument[]>();

    const groups = new Map<ClientDocumentType, ClientDocument[]>();
    for (const doc of documents) {
      const existing = groups.get(doc.documentType) || [];
      groups.set(doc.documentType, [...existing, doc]);
    }
    return groups;
  }, [documents]);

  // Télécharger un document
  const handleDownload = useCallback((doc: ClientDocument) => {
    window.open(doc.fileUrl, '_blank');
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
        <p className="text-red-600">{t('common.clientPortal.documents.errorLoading')}</p>
      </div>
    );
  }

  const hasDocuments = documents && documents.length > 0;
  const hasActiveFilters = filter.documentType || filter.search;

  return (
    <div className="space-y-4">
      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white/50 rounded-xl p-3 border border-white/60">
            <div className="flex items-center gap-2 mb-1">
              <FolderOpen className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-gray-500">{t('common.clientPortal.documents.documentsLabel')}</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{stats.totalDocuments}</p>
          </div>
          <div className="bg-white/50 rounded-xl p-3 border border-white/60">
            <div className="flex items-center gap-2 mb-1">
              <HardDrive className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-gray-500">{t('common.clientPortal.documents.totalSize')}</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{formatFileSize(stats.totalSize)}</p>
          </div>
          <div className="bg-white/50 rounded-xl p-3 border border-white/60 col-span-2">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-teal-600" />
              <span className="text-xs text-gray-500">{t('common.clientPortal.documents.byType')}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {stats.byType.slice(0, 4).map(item => (
                <span
                  key={item.type}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(item.type)}`}
                >
                  {t(DOCUMENT_TYPE_KEYS[item.type])}: {item.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Barre de recherche et filtres */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('common.clientPortal.documents.searchPlaceholder')}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500/50 focus:border-transparent transition-all duration-200"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
              filter.documentType
                ? 'bg-teal-50 border-teal-200 text-teal-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            {filter.documentType ? t(DOCUMENT_TYPE_KEYS[filter.documentType]) : t('common.clientPortal.documents.typeFilter')}
            <ChevronDown className="w-4 h-4" />
          </button>

          {showFilters && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-10">
              <button
                onClick={() => handleTypeFilter(undefined)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('common.clientPortal.documents.allTypes')}
              </button>
              {Object.entries(DOCUMENT_TYPE_KEYS).map(([type, key]) => (
                <button
                  key={type}
                  onClick={() => handleTypeFilter(type as ClientDocumentType)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                    filter.documentType === type ? 'text-teal-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  {t(key)}
                </button>
              ))}
            </div>
          )}
        </div>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
            {t('common.clientPortal.documents.clear')}
          </button>
        )}
      </div>

      {/* Liste des documents */}
      {!hasDocuments ? (
        <div className="bg-white/50 rounded-xl border border-white/60 p-8 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {hasActiveFilters
              ? t('common.clientPortal.documents.noDocumentMatch')
              : t('common.clientPortal.documents.noDocumentShared')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(groupedDocuments.entries()).map(([type, docs]) => (
            <div key={type} className="bg-white/50 rounded-xl border border-white/60 overflow-hidden">
              {/* Header du groupe */}
              <div className="bg-white/70 px-4 py-2 border-b border-white/60">
                <div className="flex items-center gap-2">
                  <span className={`p-1.5 rounded-lg ${getTypeBadgeColor(type)}`}>
                    {getDocumentIcon(type)}
                  </span>
                  <span className="font-medium text-gray-900">
                    {t(DOCUMENT_TYPE_KEYS[type])}
                  </span>
                  <span className="text-sm text-gray-500">({docs.length})</span>
                </div>
              </div>

              {/* Liste des documents du groupe */}
              <div className="divide-y divide-gray-100">
                {docs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 p-4 hover:bg-white/50 transition-colors duration-200"
                  >
                    {/* Icône et infos */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{doc.fileName}</p>
                      {doc.description && (
                        <p className="text-sm text-gray-500 truncate">{doc.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          {formatFileSize(doc.fileSize)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(doc.uploadedAt)}
                        </span>
                        {doc.uploadedByName && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {doc.uploadedByName}
                          </span>
                        )}
                        {doc.version !== '1.0' && (
                          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                            v{doc.version}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Bouton télécharger */}
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                    >
                      <Download className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('common.clientPortal.documents.download')}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectDocumentsTab;
